import { Injectable } from '@angular/core';
import {from, Observable, of} from "rxjs";

/**
 * Service for working with the device camera inside the smarthec advisor.
 */
@Injectable({
  providedIn: 'root'
})
export class CameraService {

  /**
   * the video element. We want to have this public because we need the width and height from external components.
   */
  public video;

  /**
   * Definition of the (minimum) constraints that we need for the application.
   */
  static readonly cameraConstraints: any = {
    video: {
      width: {
        min: 1280,
        ideal: 2500,
        max: 4096,
      },
      height: {
        min: 720,
        ideal: 1500,
        max: 4000
      },
      facingMode: 'environment'
    }
  }

  /**
   * Some status items which can be the result of the camera check.
   */
  static readonly STATUS_NO_PERMISSION = 'no-permission';
  static readonly STATUS_RESOLUTION_TO_LOW = 'resolution';
  static readonly STATUS_UNKNOWN_ERROR = 'unknown';
  static readonly STATUS_BROWSER_NOK = 'browser-nok';
  static readonly STATUS_OK = 'ok';

  /**
   * Test if the capabilities of the current device fullfill our (minimum) need.
   */
  testCameraCapabilities(): Observable<string> {
    if ('mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices) {

      return new Observable(observer => {
        return from(
          navigator.mediaDevices.getUserMedia(CameraService.cameraConstraints)
            .then(stream => {
              console.debug("permission granted", stream);

              // Stop all available tracks so the camera is disabled again.
              stream.getTracks().forEach(function(track) {
                track.stop();
              });

              return CameraService.STATUS_OK;
            })
            .catch(error => {
              if(typeof error === "object" && error instanceof OverconstrainedError) {
                console.debug("Device does not fit the minimum constraints");
                return CameraService.STATUS_RESOLUTION_TO_LOW;
              }
              if(typeof error === "object" && error instanceof DOMException) {
                console.debug("Permission not granted", error);
                return CameraService.STATUS_NO_PERMISSION;
              }
              else {
                console.debug("Could not access media device", error);
                return CameraService.STATUS_UNKNOWN_ERROR;
              }
            })
        ).subscribe(observer)
        }
      );
    }
    else {
      // no media stream, so the browser does not fullfill our needs
      return of(CameraService.STATUS_BROWSER_NOK);
    }
  }

  /**
   * Start the camera in a given video element
   * @param videoElement
   */
  startCamera(videoElement): Observable<void> {
    return new Observable(observer => {
        return from(
          navigator.mediaDevices.getUserMedia(CameraService.cameraConstraints)
            .then(stream => {
              this.video = (<HTMLVideoElement>document.getElementById(videoElement));
              if(this.video) {
                // this.video.srcObject = stream;
                if ('srcObject' in this.video) {
                  try {
                    this.video.srcObject = stream;
                  } catch (err) {
                    if (err.name != "TypeError") {
                      throw err;
                    }
                    // Even if they do, they may only support MediaStream
                    this.video.src = stream;
                  }
                } else {
                  this.video.src = stream;
                }

                return this.video.play();
              }
              else {
                return null;
              }
            })
            .catch(error => {
              console.error("Could not start camera.", error)
            })
        ).subscribe(observer)
      }
    );
  }

  /**
   * Stops the camera
   */
  stopCamera(): void {
    if(this.video) {
      this.video.srcObject.getTracks().forEach(function(track) {
        track.stop();
      });
    }
  }

  /**
   * Take a picture from the current media track.
   */
  takePicture(): Observable<any> {
    if(!this.video || !this.video.srcObject || !this.video.srcObject.getTracks()[0]) {
      // camera not ready
      return of(null);
    }
    const track = this.video.srcObject.getTracks()[0];

    // temp disable this function because we cannot devide taking photo and getting blob here. On slow devices this results in bad behaviour
    if(false && typeof ImageCapture === "function") {
      // Browser support imagecapture API
      const imageCapture = new ImageCapture(track);
      return new Observable(observer => {
          return from(
            imageCapture.takePhoto({fillLightMode: "flash"})
              .catch(error => console.error('takePicture() error:', error))
          ).subscribe(observer)
        }
      );
    }
    else {
      // fallback to canvas because browser does not support ImageCapture API
      return new Observable(observer => {
          return from(
            new Promise((resolve, reject) => {
              try {
                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                canvas.width = this.video.videoWidth;
                canvas.height = this.video.videoHeight;
                context.drawImage(this.video, 0, 0, this.video.videoWidth, this.video.videoHeight);
                resolve(canvas);
              } catch(error) {
                console.error("takePicture() error with canvas", error);
                reject(error);
              }
            })
          ).subscribe(observer)
        }
      );
    }
  }
}
